<template>
  <div class="w-full pl-2 pt-2">
    <div class="mx-2 mt-2 flex">
      <span class="text-l w-[90%]">
        {{ t('changeLog.generateTitle') }}
      </span>
      <div class="w-[10%]">
        <tx-dropdown
          v-if="predefinedCriteria && predefinedCriteria.length" v-model="selectedPredefinedOption"
          :items="predefinedCriteria" value-prop="name" display-prop="name" @select="onApplyPredefinedCriteria()"
        >
          <template #button>
            <icon-button
              type="inline" faicon="fa-light fa-angle-down" :label="t('general.preDefinedCriteria')"
              :right-icon="true" :width="200"
            />
          </template>
        </tx-dropdown>
      </div>
    </div>
    <!-- <div v-if="predefinedCriteria && predefinedCriteria.length" class="w-[80%] mx-2">
      <tx-select
        v-model="selectedPredefinedOption"
        :label="t('general.selectCriteria')" :data="predefinedCriteria"
        value-prop="name" display-prop="name" :clearable="true" :filterable="true"
        @change="$event => onApplyPredefinedCriteria()"
      />
    </div> -->
    <div class="w-auto flex flex-row pt-2 flex-wrap">
      <div v-for="option in props.columns" :key="option.key" class="w-[30%] mx-2 mb-2">
        <tx-select
          v-if="option.type === 'list'" v-model="modelValue[option.key]" :label="option.label" :data="option.data"
          :value-prop="option.valueProp" :display-prop="option.displayProp" :required="option.required" :clearable="option.clearable" :filterable="option.filterable"
          :errors="v$[option.key]?.$errors" :multiple-values="option.multiple" :multiple-limit="option.multipleLimit" :disabled="disableOptions[option.key]"
        />
        <tx-date-picker
          v-else-if="option.type === 'dateRange'" v-model="modelValue[option.key]" :label="option.label" :value-prop="option.valueProp" clearable
          :errors="v$[option.key]?.$errors" :display-prop="option.displayProp" range multi-calendars auto-apply :required="option.required" :disabled="disableOptions[option.key]" :max-date="new Date().toString()"
        />
        <tx-input
          v-else-if="option.type === 'textArea'" v-model="modelValue[option.key]" type="textarea" :label="option.label" :required="option.required" :clearable="option.clearable"
          :errors="v$[option.key]?.$errors" :disabled="disableOptions[option.key]"
        />
        <tx-input
          v-else v-model="modelValue[option.key]" :label="option.label" :required="option.required" :clearable="option.clearable"
          :errors="v$[option.key]?.$errors" :disabled="disableOptions[option.key]"
        />
      </div>
    </div>

    <!-- FOOTER -->
    <tx-form-footer
      class="flex flex-row justify-end flex-shrink-0 flex-nowrap"
      primary-text="Apply" :primary-disabled="v$.$invalid " :show-custom="true" custom-text="Save Criteria" :custom-disabled="v$.$invalid"
      @primary-click="getData" @secondary-click="onCancel" @custom-click="showSaveCriteriaDialog = true"
    />
    <tx-dialog
      v-model="showSaveCriteriaDialog" :show-ok-cancel="true" :title="t('dataTable.generateData.saveCriteria')" confirm-text="general.ok" width="450px" height="250px"
      :ok-state="criteriaName.length === 0 || isNameInvalid ? 'disabled' : 'enabled'" @ok="saveCriteria" @cancel="showSaveCriteriaDialog = false"
    >
      <div class="mt-6">
        <tx-input
          v-model.trim="criteriaName" :label="t('dataTable.generateData.name')" required
        />
      </div>
    </tx-dialog>
  </div>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { createI18nMessage, helpers, required } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, reactive, ref } from 'vue'
import type { ITxDataTableGenerateDataColumns } from './TxDataTable.types'
import TxSelect from '@/shared/components/TxSelect.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxDatePicker from '@/shared/components/TxDatePicker.vue'
import TxFormFooter from '@/shared/components/forms/TxFormFooter.vue'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxDropdown from '@/shared/components/TxDropdown.vue'
import IconButton from '@/shared/components/IconButton.vue'
import utils from '@/services/utils'

const props = defineProps<{
  columns: ITxDataTableGenerateDataColumns[]
  predefinedCriteria?: any[]
  currentCriteria?: any
}>()

const emit = defineEmits<{
  (e: 'getData', val: Record<string, any>): void
  (e: 'saveCriteria', val: Record<string, any>, name: string): void
  (e: 'close'): void
}>()

const { t } = useI18n()
const withI18nMessage = createI18nMessage({ t })

const disableOptions = ref<Record<string, boolean>>({})
const modelValue = reactive<Record<string, any>>({})
const selectedPredefinedOption = ref<Record<string, boolean> | null>(null)
const showSaveCriteriaDialog = ref(false)
const criteriaName = ref('')

const isNameInvalid = computed(() => {
  if (props.predefinedCriteria && criteriaName.value) {
    const predefinedCriteriaNames = props.predefinedCriteria.map(criteria => criteria.name.toString().toLowerCase())
    return predefinedCriteriaNames.includes(criteriaName.value.toString())
  }
  else {
    return true
  }
})

const rules = computed(() => {
  const result: Record<string, any> = {}
  if (props.columns) {
    props.columns.forEach((column) => {
      if (column.required) {
        result[column.key] = { required: withI18nMessage(required) }
      }
      if (column.key !== 'OutputFields' && column.key !== 'DateRange' && column.key !== 'Fields') {
        result[column.key] = {
          atLeastOne: helpers.withMessage(t('validations.atleast'), atLeastOneRequired),
        }
      }
    })
  }
  return result
})
const v$ = useVuelidate(rules, modelValue)

// Custom validator: At least one field must be filled
function atLeastOneRequired() {
  let valid = false

  Object.keys(modelValue).forEach((key) => {
    if (!valid && key !== 'OutputFields' && key !== 'DateRange' && key !== 'Fields' && modelValue[key] && modelValue[key].toString().trim() !== '') {
      valid = true
    }
  })
  return valid
}

onMounted(() => {
  resetWithDefaultValue()
  if (utils.isDefined(props.currentCriteria)) {
    props.columns.forEach((column) => {
      if (props.currentCriteria![column.key]) {
        modelValue[column.key] = props.currentCriteria![column.key]
      }
    })
  }
})
function onCancel() {
  resetWithDefaultValue()
  emit('close')
}

function resetWithDefaultValue() {
  props.columns.forEach((column) => {
    if (column.defaultValue) {
      modelValue[column.key] = column.defaultValue
    }
  })
}

function getData() {
  emit('getData', modelValue)
  modelValue.value = {}
}

function saveCriteria() {
  emit('saveCriteria', modelValue, criteriaName.value)
  showSaveCriteriaDialog.value = false
  criteriaName.value = ''
}
function onApplyPredefinedCriteria() {
  // apply the criteria and pre fill the form
  if (selectedPredefinedOption.value && props.predefinedCriteria) {
    const data = props.predefinedCriteria.find(criteria => criteria.name === selectedPredefinedOption.value)
    resetWithDefaultValue()
    props.columns.forEach((column) => {
      if (data.value[column.key]) {
        modelValue[column.key] = data.value[column.key]
      }
    })
  }
}

defineExpose({
  disableOptions,
})
</script>
